import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, ImagePlus, UploadCloud } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, mediaUrl, formatApiErrorDetail } from "@/lib/api";

const EMPTY = { title: "", application: "", material: "", technology: "", tolerance: "", production_time: "" };
const IMG_EXT = ["jpg", "jpeg", "png", "webp"];

export const PortfolioManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/portfolio").then(({ data }) => setItems(data)).catch(() => toast.error("Falha ao carregar portfólio")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickFile = (f) => {
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!IMG_EXT.includes(ext)) { toast.error("Use JPG, PNG ou WEBP."); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.application || !form.material || !form.technology) {
      toast.error("Preencha título, aplicação, material e tecnologia.");
      return;
    }
    if (!file) { toast.error("Escolha uma foto da peça."); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("image", file);
      const { data } = await api.post("/portfolio", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setItems((prev) => [data, ...prev]);
      setForm(EMPTY); setFile(null); setPreview("");
      if (inputRef.current) inputRef.current.value = "";
      toast.success("Peça adicionada ao portfólio");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha ao guardar");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/portfolio/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      toast.success("Peça removida");
    } catch {
      toast.error("Falha ao remover");
    }
  };

  return (
    <div data-testid="portfolio-manager" className="grid lg:grid-cols-3 gap-8">
      <form onSubmit={submit} data-testid="portfolio-add-form" className="lg:col-span-1 bg-ax-card border border-ax-border rounded-xl p-6 h-fit lg:sticky lg:top-24">
        <h3 className="font-heading font-bold text-lg text-ax-text flex items-center gap-2 mb-5"><Plus className="w-5 h-5 text-ax-orange" /> Nova peça</h3>

        <div
          onClick={() => inputRef.current?.click()}
          className="cursor-pointer rounded-lg border-2 border-dashed border-ax-border hover:border-ax-cyan bg-ax-surface/50 transition-colors overflow-hidden mb-4"
        >
          <input ref={inputRef} data-testid="portfolio-image-input" type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} />
          {preview ? (
            <img src={preview} alt="pré-visualização" className="w-full h-44 object-cover" />
          ) : (
            <div className="p-8 text-center">
              <ImagePlus className="w-8 h-8 mx-auto text-ax-cyan mb-2" />
              <p className="text-sm text-ax-sub">Carregar foto da peça (JPG/PNG/WEBP)</p>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <F label="Título *"><Input data-testid="portfolio-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="ax-input" placeholder="ex: Engrenagem reforçada" /></F>
          <F label="Aplicação *"><Input data-testid="portfolio-application" value={form.application} onChange={(e) => set("application", e.target.value)} className="ax-input" placeholder="ex: Manutenção industrial" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Material *"><Input data-testid="portfolio-material" value={form.material} onChange={(e) => set("material", e.target.value)} className="ax-input" placeholder="PETG-CF" /></F>
            <F label="Tecnologia *"><Input data-testid="portfolio-technology" value={form.technology} onChange={(e) => set("technology", e.target.value)} className="ax-input" placeholder="FDM" /></F>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Tolerância"><Input data-testid="portfolio-tolerance" value={form.tolerance} onChange={(e) => set("tolerance", e.target.value)} className="ax-input" placeholder="±0.15 mm" /></F>
            <F label="Tempo produção"><Input data-testid="portfolio-time" value={form.production_time} onChange={(e) => set("production_time", e.target.value)} className="ax-input" placeholder="24h" /></F>
          </div>
        </div>

        <button data-testid="portfolio-submit" disabled={saving} className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-ax-orange hover:bg-ax-orangehover disabled:opacity-60 text-white font-semibold py-3 rounded-lg transition-all">
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> A guardar...</> : <><UploadCloud className="w-4 h-4" /> Adicionar ao portfólio</>}
        </button>
      </form>

      <div className="lg:col-span-2">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-ax-orange" /></div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-5" data-testid="portfolio-manage-list">
            {items.map((it) => (
              <div key={it.id} data-testid={`portfolio-manage-${it.id}`} className="bg-ax-card border border-ax-border rounded-xl overflow-hidden group">
                <div className="relative h-40">
                  <img src={mediaUrl(it.image_url)} alt={it.title} className="w-full h-full object-cover" />
                  <button data-testid={`portfolio-delete-${it.id}`} onClick={() => remove(it.id)} className="absolute top-2 right-2 bg-ax-bg/80 hover:bg-red-500 text-white rounded-lg p-2 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <span className="absolute bottom-2 left-2 font-mono text-[11px] px-2 py-0.5 rounded bg-ax-bg/85 text-ax-cyan border border-ax-border">{it.technology}</span>
                </div>
                <div className="p-4">
                  <h4 className="font-heading font-semibold text-ax-text text-sm">{it.title}</h4>
                  <p className="text-xs text-ax-sub mt-1">{it.application}</p>
                  <p className="text-xs text-ax-muted mt-2 font-mono">{it.material} · {it.tolerance || "—"} · {it.production_time || "—"}</p>
                </div>
              </div>
            ))}
            {items.length === 0 && <p className="text-ax-muted col-span-2 text-center py-16">Sem peças. Adicione a primeira à esquerda.</p>}
          </div>
        )}
      </div>
    </div>
  );
};

const F = ({ label, children }) => (
  <div><Label className="text-xs text-ax-sub mb-1 block">{label}</Label>{children}</div>
);
